<template>
    <MainComponent
        :id="anchorsPageProfile.recommendationsSection"
        ref="recommendationSection"
        :class="{'edit-mode': isEditionEnabled, 'recommendation-section--mf': mf}"
        data-testid="recommendation-sections"
    >
        <template #title>
            <VJoyWrapper>
                <h2 class="recommendation-section__title">{{ t('profile.view.navigation.tabs.recommendations') }}</h2>
            </VJoyWrapper>
        </template>

        <template #title-action>
            <RecommendationActionButton v-if="!mf && recommendations.length > 0" @open-add-recommendation="openAddRecommendation()" />
        </template>

        <template #content>
            <RecommendationAuthors v-if="recommendations.length > 1" :recommendations="recommendations" />
            <NoRecommendation
                v-else-if="!mf && recommendations.length === 0"
                :edit-mode="isEditionEnabled"
                @open-add-recommendation="openAddRecommendation()"
            />
            <VJoyWrapper v-if="loggedIn" direction="column" class="recommendation-section__list">
                <RecommendationCard
                    v-for="recommendation in displayedRecommendations"
                    :key="recommendation.id"
                    :recommendation="recommendation"
                    :mf="mf"
                    class="recommendation-card"
                    @see-more-clicked-when-locked="$emit('seeMoreClickedWhenLocked')"
                />
            </VJoyWrapper>
            <ShowMoreByItemCount
                v-if="loggedIn"
                :label="t('profile.section.recommendation.show.all.label.v2', {count: numberOfRecommendations})"
                :limit="RECOMMENDATIONS_TO_DISPLAY_BY_DEFAULT"
                :total="numberOfRecommendations"
                test-id="read-more-recommendations"
                :locked="{
                    allowHref: !mf,
                    componentName: 'profile-recommendations-section',
                    targetId: anchorsPageProfile.recommendationsSection,
                    label: t('profile.section.recommendation.private', {0: profile?.firstname}),
                }"
                @show-more-clicked-when-locked="$emit('showMoreClickedWhenLocked')"
                @collapse="toggleShowMoreRecommendations(false)"
                @expand="toggleShowMoreRecommendations(true)"
            />
        </template>
    </MainComponent>
</template>
<script lang="ts" setup>
    import {computed, nextTick, onMounted, ref, onUnmounted, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useGuestHost, navigateTo, useRoute, useProfilePageViewMode, useTranslation, useAuth} from '#imports';
    import {VJoyWrapper} from '@maltjoy/core-vue';
    import ShowMoreByItemCount from '~/components/_common/show-more/ShowMoreByItemCount.vue';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import {useExtractQueryParameters} from '~/composables/useUrlUtils';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import NoRecommendation from '~/features/recommendations/ui/NoRecommendation.vue';
    import {useProfileStore} from '~/store/profileStore';
    import {useRecommendationsStore} from './store/recommendations.store';
    import RecommendationActionButton from './ui/RecommendationActionButton.vue';
    import RecommendationAuthors from './ui/RecommendationAuthors.vue';
    import RecommendationCard from './ui/RecommendationCard.vue';

    // Props
    interface RecommendationsSectionProps {
        mf?: boolean;
    }
    const {mf = false} = defineProps<RecommendationsSectionProps>();

    defineEmits<{
        showMoreClickedWhenLocked: [];
        seeMoreClickedWhenLocked: [];
    }>();

    // Constants
    const RECOMMENDATIONS_TO_DISPLAY_BY_DEFAULT = 3;

    // Stores
    const {profile} = storeToRefs(useProfileStore());
    const {recommendationsAcceptedWithTextContent} = storeToRefs(useRecommendationsStore());

    // Composables
    const {loggedIn, user} = useAuth();
    const {t} = useTranslation();
    const {isEditionEnabled} = useProfilePageViewMode();
    const recommendationSection = useTemplateRef('recommendationSection');
    const recommendations = computed(() =>
        recommendationsAcceptedWithTextContent.value
            .filter((recommendation) => {
                const userRecommendation = user?.id == recommendation.accountId;
                return userRecommendation || !recommendation.hidden || isEditionEnabled.value;
            })
            .sort((r1, r2) => {
                const [r1IsUser, r2IsUser] = [user?.id == r1.accountId, user?.id == r2.accountId];
                if (r1IsUser !== r2IsUser) return r1IsUser ? -1 : 1;
                const d1 = r1.lastUpdateDate ? new Date(r1.lastUpdateDate).getTime() : 0;
                const d2 = r2.lastUpdateDate ? new Date(r2.lastUpdateDate).getTime() : 0;
                return d2 - d1;
            }),
    );

    // State
    const isShowingAllRecommendations = ref(false);

    // Computed
    const displayedRecommendations = computed(() =>
        isShowingAllRecommendations.value ? recommendations.value : recommendations.value.slice(0, RECOMMENDATIONS_TO_DISPLAY_BY_DEFAULT),
    );

    const numberOfRecommendations = computed(() => recommendations.value.length);

    // Methods
    function toggleShowMoreRecommendations(shouldShow: boolean) {
        isShowingAllRecommendations.value = shouldShow;
    }

    async function openAddRecommendation() {
        if (!loggedIn) {
            const route = useRoute();
            const host = useGuestHost();

            const queryParams = new URLSearchParams(useExtractQueryParameters(route));
            queryParams.set('drawerOpen', 'true');

            const redirectUriParams = encodeURIComponent(`${location.pathname}?${queryParams}#${anchorsPageProfile.recommendationsSection}`);
            const redirectUri = `${host}/signin?redirect=${redirectUriParams}`;
            await navigateTo(redirectUri, {external: true});
        } else {
            eventBus.emit('external-action:recommendations:add');
        }
    }

    function scrollToRecommendationsSection() {
        nextTick(() => {
            setTimeout(() => {
                recommendationSection.value?.$el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 600);
        });
    }

    onMounted(() => {
        eventBus.on('main:recommendations:scroll-to', scrollToRecommendationsSection);
        eventBus.emit('main:recommendations:ready');
    });

    onUnmounted(() => {
        eventBus.off('main:recommendations:scroll-to', scrollToRecommendationsSection);
    });
</script>
<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .recommendation-section {
        &--mf {
            border: none !important;
            border-radius: 0 !important;
            border-bottom: 1px solid var(--joy-color-neutral-30) !important;
            padding-bottom: var(--joy-core-spacing-4);

            .recommendation-section__title {
                font-size: var(--joy-font-size-primary-400);
            }
        }

        &__list {
            margin-top: 0 !important;

            .recommendation-card:is(:first-child) {
                margin-top: var(--joy-core-spacing-6);
            }

            .recommendation-card:is(:last-child) {
                margin-bottom: 0;
                padding-bottom: 0;
            }

            .recommendation-card:not(:last-child) {
                border-bottom: 1px solid var(--joy-color-neutral-30);
                margin-bottom: 0;
                padding-bottom: var(--joy-core-spacing-6);
            }
        }
    }
</style>
